import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { JSONDecryptor, EncryptedData } from '@utils/encryptorUtil';
import { errorLogger } from '@utils/applicationLogUtils';

const GlobalRenderController: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [renderConfig, setRenderConfig] = useState<any>({});

  useEffect(() => {
    // Load and decrypt render config
    const loadRenderConfig = async () => {
      try {
        const response = await fetch('/config/renderConfig.json');
        if (!response.ok) {
          throw new Error('Failed to load render config');
        }
        const encryptedData: EncryptedData = await response.json();
        const decryptor = new JSONDecryptor(process.env.SECRET_KEY || '');
        const decodedConfig = await decryptor.decrypt(encryptedData);
        setRenderConfig(decodedConfig);
      } catch (error) {
        errorLogger('Failed to load render config:', error);
        setRenderConfig({});
      }
    };
    
    loadRenderConfig();
  }, []);

  useEffect(() => {
    if (Object.keys(renderConfig).length === 0) return;
    // Find matching route
    const routeKey = Object.keys(renderConfig).find(route => {
      if (route.includes(':')) {
        const routePattern = route.replace(/:[^/]+/g, '[^/]+');
        return new RegExp(`^${routePattern}$`).test(location.pathname);
      }
      return route === location.pathname;
    });

    if (!routeKey) return;

    const config = (renderConfig as any)[routeKey];
    
    // Generate CSS to hide elements
    const generateHideCSS = (obj: any): string => {
      let css = '';
      
      Object.entries(obj).forEach(([key, value]) => {
        if (value === false) {
          // If key starts with '.', it's a CSS selector
          if (key.startsWith('.')) {
            css += `${key} { display: none !important; }\n`;
          } else {
            // Otherwise, treat as component name
            css += `[data-component="${key}"] { display: none !important; }\n`;
          }
        } else if (typeof value === 'object' && value !== null) {
          css += generateHideCSS(value);
        }
      });
      
      return css;
    };

    // Remove existing styles
    const existingStyle = document.getElementById('global-render-styles');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Inject new styles
    const css = generateHideCSS(config);
    if (css) {
      const style = document.createElement('style');
      style.id = 'global-render-styles';
      style.textContent = css;
      document.head.appendChild(style);
    }

    return () => {
      const styleElement = document.getElementById('global-render-styles');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [location.pathname, renderConfig]);

  return <>{children}</>;
};

export default GlobalRenderController;